import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import find from 'lodash/find';
import union from 'lodash/union';
import take from 'lodash/take';
import ScrollLazyLoad from '@Components/Results/utils/scroll-lazy-load.component';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import Breadcrumb from '@Services/Breadcrumb';
import Entries from '@Services/Entries';
import ReactionTopButtons from '@Components/Results/reactions/reaction-details/ReactionTopButtons';
import ReactionDetailsTable from '@Components/Results/reactions/reaction-details/ReactionDetailsTable';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { withToggleConditionsGlobalConsumer } from '@Pages/Results/ToggleConditionsGlobal';

export class ReactionDetailsComponent extends React.Component {
  // if do not show all conditions
  static MINIMAL_VIEW_MAX_CONDITIONS_COUNT = 3;

  static _defaultDetailsLimit(detailsCount, isViewPreview = false, isMinimalView = false) {
    // eslint-disable-next-line no-nested-ternary
    return isViewPreview
      ? Math.min(detailsCount, 100)
      : detailsCount <= 5 && !isMinimalView
        ? detailsCount
        : ReactionDetailsComponent.MINIMAL_VIEW_MAX_CONDITIONS_COUNT;
  }

  constructor(props) {
    super(props);

    const { metadata, reaction, displayAllConditions } = props;

    const conditionsState = Breadcrumb.getResultsStateForDb(metadata.databaseId, 'reactionConditions');

    const onResultsPage = !metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT;

    const isDetailsOpen = (type) => {
      // check restoring show/hide reaction conditions only on result page
      const checkRestore = onResultsPage && conditionsState;

      // restore of conditions state for reaction with the same id
      if (checkRestore && conditionsState[metadata.id]) {
        return conditionsState[metadata.id][type];
        // hide conditions if global toggle was activated and there is no manual state for reaction with this id
      }
      if (checkRestore && !conditionsState[metadata.id] && !conditionsState.show) {
        return false;
      }
      // all other cases for results/preview page or reactions in other contexts
      switch (type) {
        case 'isHitDetailsOpen':
          return !metadata.fromDocuments && !metadata.fromRT;

        case 'isAllDetailsOpen':
          return !metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT && isEmpty(props.reaction.hitDetails);
        default:
          return undefined;
      }
    };

    this.state = {
      details:
        isArray(reaction.details) && reaction.details.length
          ? ((details) => {
              const limit = this.constructor._defaultDetailsLimit(reaction.detailsCount, metadata.isViewPreview, !displayAllConditions);
              if (details.length > limit) {
                details.splice(limit);
              }

              details.forEach((item) => {
                if (isArray(item.yields)) {
                  // eslint-disable-next-line no-param-reassign
                  item.yields = map(reaction.products, (product) => find(item.yields, { id: product.details.id }));
                }
              });
              return details;
            })(props.reaction.details)
          : [],
      hitDetails:
        reaction.hitDetails &&
        ((hitDetails) => {
          const hitsLimit = this.constructor._defaultDetailsLimit(reaction.hitDetailsCount, metadata.isViewPreview, !displayAllConditions);
          if (hitDetails.length > hitsLimit) {
            hitDetails.splice(hitsLimit);
          }

          hitDetails.forEach((item) => {
            if (isArray(item.yields)) {
              // eslint-disable-next-line no-param-reassign
              item.yields = map(reaction.products, (product) => find(item.yields, { id: product.details.id }));
            }
          });
          return hitDetails;
        })(reaction.hitDetails),
      isHitDetailsOpen: isDetailsOpen('isHitDetailsOpen'),
      isAllDetailsOpen: isDetailsOpen('isAllDetailsOpen'),
    };

    const { details, hitDetails } = this.state;

    this.initialConditionsNumber = (details && details.length) || 0;
    this.initialHitsNumber = (hitDetails && hitDetails.length) || 0;

    this.isDetailsLoading = false;

    this.toggleHitDetailsOpen = this.toggleHitDetailsOpen.bind(this);
    this.toggleAllDetailsOpen = this.toggleAllDetailsOpen.bind(this);
    this.loadInitialReactionDetails = this.loadInitialReactionDetails.bind(this);
    this.showMoreReactionDetails = this.showMoreReactionDetails.bind(this);
    this.showLessReactionDetails = this.showLessReactionDetails.bind(this);
  }

  componentDidMount() {
    const { metadata } = this.props;

    if (metadata.expandConditions) {
      this.toggleAllDetailsOpen();
    }
  }

  componentDidUpdate(prevProps) {
    const { metadata, showConditions } = this.props;
    const { isAllDetailsOpen, isHitDetailsOpen } = this.state;
    const conditionsState = Breadcrumb.getResultsStateForDb(metadata.databaseId, 'reactionConditions') || {};

    conditionsState[metadata.id] = {
      isAllDetailsOpen,
      isHitDetailsOpen,
    };

    if (!metadata.isViewPreview && !metadata.fromDocuments && !metadata.fromRT) {
      Breadcrumb.updateResultsStateForDb(metadata.databaseId, 'reactionConditions', conditionsState);
    }

    if (prevProps.showConditions !== showConditions) {
      this.toggleConditions(showConditions);
    }
  }

  getHitDetailsTable() {
    const { metadata, reaction, spinnerId, displayAllConditions } = this.props;
    const { hitDetails, isAllDetailsOpen } = this.state;
    return !isEmpty(hitDetails) ? (
      <ReactionDetailsTable
        metadata={metadata}
        reactionId={reaction.id}
        databaseId={reaction.databaseId}
        details={hitDetails}
        detailsCount={reaction.hitDetailsCount}
        largestProduct={reaction.largestProduct}
        initialDetailsNumber={this.initialHitsNumber}
        isHitDetails
        hasHitDetails
        spinnerId={spinnerId}
        index={reaction.index}
        showMoreHandler={({ firstItem, lastItem }) => this.showMoreReactionDetails(true, firstItem, lastItem)}
        showLessHandler={() => this.showLessReactionDetails(true)}
        showNextConditions={
          reaction.hitDetailsCount !== reaction.detailsCount &&
          !isAllDetailsOpen &&
          `Show ${reaction.detailsCount > 100 ? 'next 100' : 'all'} conditions`
        }
        toggleAllDetails={this.toggleAllDetailsOpen}
        displayFooter={displayAllConditions}
      />
    ) : null;
  }

  getDetailsTable() {
    const { metadata, reaction, spinnerId, displayAllConditions } = this.props;
    const { details } = this.state;
    return !isEmpty(details) ? (
      <ReactionDetailsTable
        metadata={metadata}
        reactionId={reaction.id}
        databaseId={reaction.databaseId}
        details={details}
        detailsCount={reaction.detailsCount}
        largestProduct={reaction.largestProduct}
        initialDetailsNumber={this.initialConditionsNumber}
        isHitDetails={false}
        hasHitDetails={reaction.hitDetailsCount > 0}
        spinnerId={spinnerId}
        index={reaction.index}
        showMoreHandler={({ firstItem, lastItem }) => this.showMoreReactionDetails(false, firstItem, lastItem)}
        showLessHandler={() => this.showLessReactionDetails(false)}
        showNextConditions={false}
        displayFooter={displayAllConditions}
      />
    ) : (
      <ScrollLazyLoad load={this.loadInitialReactionDetails} viewportOffset={1} container=".results-wrapper" />
    );
  }

  toggleConditions(show) {
    const { reaction } = this.props;
    this.setState({
      isAllDetailsOpen: show ? isEmpty(reaction.hitDetails) : false,
      isHitDetailsOpen: show,
    });
  }

  toggleHitDetailsOpen() {
    this.setState((prevState) => {
      return {
        isHitDetailsOpen: !prevState.isHitDetailsOpen,
      };
    });
  }

  toggleAllDetailsOpen() {
    this.setState((prevState) => {
      return {
        isAllDetailsOpen: !prevState.isAllDetailsOpen,
      };
    });
  }

  loadInitialReactionDetails() {
    const { metadata, reaction, displayAllConditions } = this.props;
    const { details } = this.state;
    let limit = Math.min(metadata.detailsCount, resultsConfig.INITIAL_ITEMS_AMOUNT.reactionDetails);
    if (isEmpty(details)) {
      // case lazyload

      if (reaction.hitDetailsCount === 0) {
        limit = this.constructor._defaultDetailsLimit(reaction.detailsCount, metadata.isViewPreview, !displayAllConditions);
        this.initialConditionsNumber = limit;
      }

      return this.showMoreReactionDetails(false, 1, limit);
    }
    if (details.length <= limit) {
      // case no lazyload, retrieved as combinedResponse
      this.initialConditionsNumber = 0;
      this.initialHitsNumber = 0;
      return this.showMoreReactionDetails(false, details.length + 1, limit);
    }

    return new Promise((resolve) => {
      resolve(details);
    });
  }

  showMoreReactionDetails(hitData, firstItem = 1, lastItem = 100) {
    if (!this.isDetailsLoading) {
      this.isDetailsLoading = true;

      const { metadata } = this.props;
      const { resultName, order, sort } = metadata;
      const {
        reaction: { index, databaseId = getValueByNs(this, 'props.metadata.databaseId') },
      } = this.props;

      return Entries.getReactionDetails({ index, resultName, databaseId, firstItem, lastItem, order, sort, hitData })
        .then((data) => {
          const { hitDetails = [], details = [] } = this.state;
          const { reaction } = this.props;

          if (isArray(data)) {
            if (hitData) {
              this.setState({ hitDetails: union(hitDetails, data) });
            } else {
              data.forEach((item) => {
                if (isArray(item.yields)) {
                  // eslint-disable-next-line no-param-reassign
                  item.yields = map(reaction.products, (product) => find(item.yields, { id: product.details.id }));
                }
              });
              this.setState({ details: union(details, data) });
            }
          }
        })
        .finally(() => {
          this.isDetailsLoading = false;
        })
        .catch(Logger.reject);
    }

    return Promise.resolve();
  }

  showLessReactionDetails(hitData) {
    const { hitDetails, details } = this.state;

    if (hitData) {
      this.setState({
        hitDetails: take(hitDetails, this.initialHitsNumber),
      });
    } else {
      const firstN = take(details, this.initialConditionsNumber);
      const state = {
        details: firstN,
        ...(isEmpty(firstN) && { isAllDetailsOpen: false }),
      };
      this.setState(state);
    }
  }

  render() {
    const { displayAllConditions, metadata, reaction, spinnerId } = this.props;
    const { isHitDetailsOpen, isAllDetailsOpen } = this.state;

    return (
      <div className="react-reaction-details-wrapper">
        {displayAllConditions && (
          <ReactionTopButtons
            metadata={metadata}
            reaction={reaction}
            index={reaction.index}
            isHitDetailsOpen={isHitDetailsOpen}
            isAllDetailsOpen={isAllDetailsOpen}
            toggleHitDetailsOpen={this.toggleHitDetailsOpen}
            toggleAllDetailsOpen={this.toggleAllDetailsOpen}
            spinnerId={spinnerId}
          />
        )}
        {reaction.hitDetailsCount > 0 && isHitDetailsOpen && this.getHitDetailsTable()}
        {reaction.detailsCount > 0 && isAllDetailsOpen && this.getDetailsTable()}
      </div>
    );
  }
}

ReactionDetailsComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  reaction: PropTypes.object.isRequired,
  spinnerId: PropTypes.string.isRequired,
  databaseId: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  documentResults: PropTypes.bool,
  displayAllConditions: PropTypes.bool,
  showConditions: PropTypes.bool,
};

export default withToggleConditionsGlobalConsumer()(ReactionDetailsComponent);
